import clsx from 'clsx';
import { getImageUrl } from '@/utils/images';
import { useRef, useState, useEffect } from 'react';

const LG_SIZE = 1024;

type CarouselItems = {
    image?: {
        url: string;
        alt: string;
        width: number;
        height: number;
    };
    link?: {
        url: string;
        rel?: string;
        title: string;
        target?: string;
    };
    title?: string;
    description?: string;
};

export type CarouselProps = {
    slides: CarouselItems[];
};

export const Carousel = ({ slides = [] }: CarouselProps) => {
    const desktopRef = useRef<HTMLDivElement | null>(null);
    const mobileRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [index, setIndex] = useState(0);
    const [canGoLeft, setCanGoLeft] = useState(false);
    const [canGoRight, setCanGoRight] = useState(false);
    const [active, setActive] = useState(-1);

    // These are only used for the desktop version. The ohter versions are easier to calculate with CSS alone
    const showItems = Math.min(4, slides.length);
    let bigSize = 30;
    if (slides.length == 4) {
        bigSize = 40;
    } else if (showItems <= 2) {
        bigSize = 65;
    } else if (showItems <= 3) {
        bigSize = 53;
    }
    const inactiveSize = 100 / slides.length;
    const delta = slides.length - 1;
    const totalOutside = (slides.length - showItems) * inactiveSize;
    const smallSize = (100 - totalOutside - bigSize) / delta;
    const totalSize = (100 / showItems) * slides.length;

    const getItems = (target: 'sm' | 'md' | 'lg') => {
        const carouselItems = slides.map((item, itemIndex) => {
            const inViewport = itemIndex >= index && itemIndex < index + showItems;

            const style: {
                transition?: string;
                minWidth?: string;
                maxWidth?: string;
            } = {};

            const buffer = 16; //slides.length <= 4 ? '10.5' : '16';

            // Everything behaves differently depending on the size of the device, and we try to render as much as possbile without javascript.
            // We only use javascript on scroll and hover, which this part doesnt need to render the first time.
            if (target === 'sm') {
                style.minWidth = 'calc(100vw - 32px)';
                style.maxWidth = 'calc(100vw - 32px)';
            } else if (target === 'md') {
                style.minWidth = '50%';
            } else {
                style.minWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                style.transition = 'all ease-in-out 400ms';
                if (!inViewport) {
                    style.minWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                    style.maxWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                    delete style.transition;
                } else {
                    if (itemIndex === active) {
                        style.minWidth = `calc(${bigSize}% - ${0}px)`;
                    } else if (active !== -1) {
                        style.minWidth = `calc(${smallSize}% - ${buffer}px)`;
                    }
                }
            }

            const linkTarget = item.link?.target
                ? {
                      target: item.link.target,
                  }
                : {};

            return (
                <a
                    {...linkTarget}
                    key={item.title || '' + itemIndex}
                    href={item.link?.url}
                    rel={item.link?.rel || ''}
                    onMouseOut={() => setActive(-1)}
                    target={item.link?.target || '_self'}
                    onMouseOver={() => setActive(itemIndex)}
                    className="r-group r-flex r-min-h-[450px] r-flex-1 r-snap-start r-flex-col r-overflow-hidden r-bg-white r-duration-300 lg:r-max-h-[450px] lg:r-w-full"
                    style={style}
                >
                    <div
                        role="img"
                        aria-label={item.image?.alt || ''}
                        style={{ backgroundImage: `url(${getImageUrl(item.image?.url)})` }}
                        className="r-max-h-[350px] r-min-h-[350px] r-w-full r-flex-1 r-rounded-xl r-bg-cover r-bg-center r-p-2"
                    />
                    <div className="r-p-4">
                        <div className="r-text-lg r-font-bold">{item.title}</div>
                        <div className="r-overflow-hidden r-transition-all r-duration-300 lg:r-max-h-0 lg:group-hover:r-max-h-8">
                            <div className="r-pb-1 r-text-sm">{item.description}</div>
                        </div>
                        <div className="r-text-black">{item.link?.title}</div>
                    </div>
                </a>
            );
        });

        return carouselItems;
    };

    const slide = (direction: number) => {
        if (!desktopRef?.current || !containerRef?.current || !mobileRef?.current) {
            return;
        }
        if (direction > 0 && !canGoRight) {
            return;
        }
        if (direction < 0 && !canGoLeft) {
            return;
        }
        const ref = containerRef!.current!.offsetWidth >= LG_SIZE ? desktopRef!.current! : mobileRef!.current!;
        const scrollAmount = ref.clientWidth >= LG_SIZE ? ref.clientWidth / showItems : ref.clientWidth;
        ref.scrollBy({ left: direction * scrollAmount + 16 * direction + 1 * direction, behavior: 'smooth' });
    };

    const handleScroll = () => {
        const ref = containerRef!.current!.offsetWidth >= LG_SIZE ? desktopRef!.current! : mobileRef!.current!;
        const left = ref.scrollLeft > 0;
        const right = ref.scrollLeft < ref.scrollWidth - containerRef!.current!.offsetWidth;
        const itemWidth = containerRef!.current!.offsetWidth / showItems;

        setCanGoLeft(left);
        setCanGoRight(right);

        // set the correct activet item so we can calculate later whats offscreen
        // We do do this on scroll so that even when someone scrolls, we know where we are
        setIndex(Math.floor(ref.scrollLeft / itemWidth));
    };

    useEffect(() => {
        if (!desktopRef?.current || !containerRef?.current || !mobileRef?.current) {
            return;
        }
        mobileRef.current.addEventListener('scroll', handleScroll);
        desktopRef.current.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => {
            mobileRef.current?.removeEventListener('scroll', handleScroll);
            desktopRef.current?.removeEventListener('scroll', handleScroll);
        };
    }, [desktopRef, containerRef, mobileRef]);

    const leftButtonClasses = clsx(
        'md:r-hidden lg:r-block r-absolute r-left-0 r-top-1/2 r--translate-y-[74px] lg:r--translate-y-[70px] r-rounded-full r-bg-white r-shadow r-z-10 r-p-2 r-translate-x-7 lg:r-translate-x-10 r-transition-opacity',
        {
            'r-opacity-0': !canGoLeft,
        }
    );

    const rightButtonClasses = clsx(
        'md:r-hidden lg:r-block r-absolute r-right-0 r-top-1/2 r--translate-y-[74px] lg:r--translate-y-[70px] r-rounded-full r-bg-white r-shadow r-z-10 r-p-2 r--translate-x-7 lg:r--translate-x-10 r-transition-opacity',
        {
            'r-opacity-0': !canGoRight,
        }
    );

    return (
        <div className="r-flex" ref={containerRef}>
            <button onClick={() => slide(-1)} className={leftButtonClasses}>
                <svg className="r-h-6 r-w-6 r-pr-[2px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M15.7071 4.29289C16.0976 4.68342 16.0976 5.31658 15.7071 5.70711L9.41421 12L15.7071 18.2929C16.0976 18.6834 16.0976 19.3166 15.7071 19.7071C15.3166 20.0976 14.6834 20.0976 14.2929 19.7071L7.29289 12.7071C7.10536 12.5196 7 12.2652 7 12C7 11.7348 7.10536 11.4804 7.29289 11.2929L14.2929 4.29289C14.6834 3.90237 15.3166 3.90237 15.7071 4.29289Z"
                        fill="#000000"
                    />
                </svg>
            </button>

            <button onClick={() => slide(1)} className={rightButtonClasses}>
                <svg className="r-h-6 r-w-6 r-pl-[2px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.29289 4.29289C8.68342 3.90237 9.31658 3.90237 9.70711 4.29289L16.7071 11.2929C17.0976 11.6834 17.0976 12.3166 16.7071 12.7071L9.70711 19.7071C9.31658 20.0976 8.68342 20.0976 8.29289 19.7071C7.90237 19.3166 7.90237 18.6834 8.29289 18.2929L14.5858 12L8.29289 5.70711C7.90237 5.31658 7.90237 4.68342 8.29289 4.29289Z"
                        fill="#000000"
                    />
                </svg>
            </button>

            {/* Its not the most beautiful solution, having HTML for each scenario, but it has been done to avoid having calculations in javascript that will make it more compliacted with SSR */}
            <div
                ref={mobileRef}
                className="scrollbar-hide r-flex r-min-w-[calc(100vw-32px)] r-max-w-[calc(100vw-32px)] r-snap-x r-snap-mandatory r-gap-4 r-overflow-x-scroll r-transition-all r-duration-300 md:r-hidden"
            >
                {getItems('sm')}
            </div>
            <div className="r-hidden r-w-full r-gap-4 r-gap-y-8 r-overflow-x-auto r-scroll-smooth r-transition-all r-duration-300 md:r-grid md:r-flex-none md:r-grid-cols-2 lg:r-hidden">
                {getItems('md')}
            </div>

            {/* This is a special case because we dont want the hover effect to scroll the container so we wrap it one div and scroll everything together */}
            <div className="scrollbar-hide r-w-full r-snap-x r-snap-mandatory r-overflow-x-scroll" ref={desktopRef}>
                <div
                    style={{ width: `calc(${totalSize}%)` }}
                    className="r-hidden r-snap-x r-snap-start r-gap-4 r-scroll-smooth r-transition-all r-duration-300 lg:r-flex"
                >
                    {getItems('lg')}
                </div>
            </div>
        </div>
    );
};
